import { useState } from "react";
import { Phone, MessageCircle, Facebook, Instagram, Mail, MapPin, Send, CheckCircle2 } from "lucide-react";
import { waLink, PHONE, PHONE_DISPLAY, FACEBOOK_URL, INSTAGRAM_URL, EMAIL } from "../lib/constants";

export default function Contact() {
  const [form, setForm] = useState({ nombre: "", telefono: "", proyecto: "", mensaje: "" });
  const [sent, setSent] = useState(false);

  const onChange = (e) => setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    const text = `Hola! Soy ${form.nombre || "(sin nombre)"}.
Tipo de proyecto: ${form.proyecto || "(no especificado)"}.
Teléfono: ${form.telefono || "(sin teléfono)"}.

${form.mensaje || "Me gustaría solicitar un presupuesto."}`;
    setSent(true);
    window.open(waLink(text), "_blank", "noopener,noreferrer");
  };

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="relative bg-[var(--fb-bg-alt)] py-20 md:py-32"
    >
      <div className="fb-container grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5 min-w-0 reveal">
          <span className="eyebrow">Contacto</span>
          <h2 className="mt-5 font-display text-4xl md:text-5xl lg:text-6xl text-[var(--fb-ink)] font-medium tracking-tight balance leading-[1.02]">
            Hablemos de
            <br /> tu proyecto.
          </h2>
          <p className="mt-6 text-[var(--fb-ink-soft)] text-base md:text-[17px] max-w-md pretty">
            La forma más rápida de contactarnos es por WhatsApp. También podés
            escribirnos desde el formulario y te respondemos lo antes posible.
          </p>

          <ul className="mt-10 space-y-3">
            <li>
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-link"
                className="flex items-center gap-4 bg-white border border-[var(--fb-line)] hover:border-[var(--fb-ink)] rounded-2xl p-4 transition-colors group"
              >
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--fb-whatsapp)]/15 text-[var(--fb-whatsapp)]">
                  <MessageCircle className="h-5 w-5" strokeWidth={2} />
                </span>
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-[0.18em] text-[var(--fb-mute)]">
                    WhatsApp
                  </div>
                  <div className="font-medium text-[var(--fb-ink)]">
                    {PHONE_DISPLAY}
                  </div>
                </div>
                <span className="text-sm text-[var(--fb-ink)] group-hover:text-[var(--fb-accent)] transition-colors">
                  Enviar →
                </span>
              </a>
            </li>
            <li>
              <a
                href={`tel:${PHONE.replace(/\s+/g, "")}`}
                data-testid="contact-phone-link"
                className="flex items-center gap-4 bg-white border border-[var(--fb-line)] hover:border-[var(--fb-ink)] rounded-2xl p-4 transition-colors"
              >
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--fb-bg-alt)] text-[var(--fb-ink)]">
                  <Phone className="h-5 w-5" strokeWidth={2} />
                </span>
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-[0.18em] text-[var(--fb-mute)]">
                    Teléfono
                  </div>
                  <div className="font-medium text-[var(--fb-ink)]">
                    {PHONE_DISPLAY}
                  </div>
                </div>
                <span className="text-sm text-[var(--fb-ink)]">Llamar →</span>
              </a>
            </li>
            <li>
              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-facebook-link"
                className="flex items-center gap-4 bg-white border border-[var(--fb-line)] hover:border-[var(--fb-ink)] rounded-2xl p-4 transition-colors"
              >
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#1877F2]/10 text-[#1877F2]">
                  <Facebook className="h-5 w-5" strokeWidth={2} />
                </span>
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-[0.18em] text-[var(--fb-mute)]">
                    Facebook
                  </div>
                  <div className="font-medium text-[var(--fb-ink)]">
                    Ver perfil y trabajos
                  </div>
                </div>
                <span className="text-sm text-[var(--fb-ink)]">Abrir →</span>
              </a>
            </li>
            <li>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-instagram-link"
                className="flex items-center gap-4 bg-white border border-[var(--fb-line)] hover:border-[var(--fb-ink)] rounded-2xl p-4 transition-colors"
              >
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#E1306C]/10 text-[#E1306C]">
                  <Instagram className="h-5 w-5" strokeWidth={2} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-[0.18em] text-[var(--fb-mute)]">
                    Instagram
                  </div>
                  <div className="font-medium text-[var(--fb-ink)] truncate">
                    @florentincontractors
                  </div>
                </div>
                <span className="text-sm text-[var(--fb-ink)]">Abrir →</span>
              </a>
            </li>
            <li>
              <a
                href={`mailto:${EMAIL}`}
                data-testid="contact-email-link"
                className="flex items-center gap-4 bg-white border border-[var(--fb-line)] hover:border-[var(--fb-ink)] rounded-2xl p-4 transition-colors"
              >
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--fb-bg-alt)] text-[var(--fb-ink)]">
                  <Mail className="h-5 w-5" strokeWidth={2} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-[0.18em] text-[var(--fb-mute)]">
                    Email
                  </div>
                  <div className="font-medium text-[var(--fb-ink)] truncate">
                    {EMAIL}
                  </div>
                </div>
                <span className="text-sm text-[var(--fb-ink)] shrink-0">Enviar →</span>
              </a>
            </li>
            <li>
              <div className="flex items-center gap-4 bg-white border border-[var(--fb-line)] rounded-2xl p-4">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--fb-bg-alt)] text-[var(--fb-ink)]">
                  <MapPin className="h-5 w-5" strokeWidth={2} />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-[var(--fb-mute)]">
                    Zona de trabajo
                  </div>
                  <div className="font-medium text-[var(--fb-ink)]">
                    Argentina · Consultar zona
                  </div>
                </div>
              </div>
            </li>
          </ul>
        </div>

        <div
          className="lg:col-span-7 reveal"
          style={{ transitionDelay: "120ms" }}
        >
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="bg-white rounded-3xl border border-[var(--fb-line)] p-6 md:p-10 shadow-[0_24px_48px_-32px_rgba(15,17,21,0.18)]"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <Field
                label="Nombre"
                name="nombre"
                placeholder="Tu nombre"
                value={form.nombre}
                onChange={onChange}
                required
              />
              <Field
                label="Teléfono"
                name="telefono"
                type="tel"
                placeholder="Ej: 11 2345 6789"
                value={form.telefono}
                onChange={onChange}
              />
            </div>
            <div className="mt-4">
              <label className="block text-xs font-semibold tracking-[0.16em] uppercase text-[var(--fb-mute)] mb-2">
                Tipo de proyecto
              </label>
              <select
                name="proyecto"
                value={form.proyecto}
                onChange={onChange}
                data-testid="contact-project-select"
                className="w-full bg-[var(--fb-bg)] border border-[var(--fb-line)] rounded-xl px-4 py-3.5 text-[var(--fb-ink)] focus:outline-none focus:border-[var(--fb-ink)] transition-colors"
              >
                <option value="">Seleccioná una opción</option>
                <option>Pérgola</option>
                <option>Construcción general</option>
                <option>Refacción</option>
                <option>Remodelación</option>
                <option>Obra comercial</option>
                <option>Mantenimiento</option>
                <option>Otro</option>
              </select>
            </div>
            <div className="mt-4">
              <label className="block text-xs font-semibold tracking-[0.16em] uppercase text-[var(--fb-mute)] mb-2">
                Mensaje
              </label>
              <textarea
                name="mensaje"
                value={form.mensaje}
                onChange={onChange}
                rows={5}
                placeholder="Contanos brevemente qué necesitás..."
                data-testid="contact-message-input"
                className="w-full bg-[var(--fb-bg)] border border-[var(--fb-line)] rounded-xl px-4 py-3.5 text-[var(--fb-ink)] focus:outline-none focus:border-[var(--fb-ink)] transition-colors resize-none"
              />
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
              <p className="text-xs text-[var(--fb-mute)] max-w-[28ch]">
                Al enviar abrimos WhatsApp con tu mensaje listo para mandarnos.
              </p>
              <button
                type="submit"
                data-testid="contact-submit-btn"
                className="btn-primary w-full sm:w-auto"
              >
                {sent ? (
                  <>
                    <CheckCircle2 className="h-5 w-5" />
                    Enviado por WhatsApp
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Enviar por WhatsApp
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, type = "text", placeholder, value, onChange, required }) {
  return (
    <div>
      <label className="block text-xs font-semibold tracking-[0.16em] uppercase text-[var(--fb-mute)] mb-2">
        {label} {required && <span className="text-[var(--fb-accent)]">*</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        data-testid={`contact-${name}-input`}
        className="w-full bg-[var(--fb-bg)] border border-[var(--fb-line)] rounded-xl px-4 py-3.5 text-[var(--fb-ink)] placeholder:text-[var(--fb-mute)]/70 focus:outline-none focus:border-[var(--fb-ink)] transition-colors"
      />
    </div>
  );
}
